import { memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { TransactionsFilterBarProps } from '../../../types/dashboardV2';
import { FilterBar as GenericFilterBar } from '../../../components/common/FilterBar/FilterBar';
import { DateRangePicker } from '../../../components/common/DateRangePicker/DateRangePicker';
import { StatusFilterDropdown } from '../../../components/common/StatusFilterDropdown/StatusFilterDropdown';
import { FilterToggle } from '../../../components/common/FilterToggle/FilterToggle';
import { TRANSACTION_STATUS_OPTIONS } from '../../../constants/filterOptions';
import { TransactionStatus } from '../../../shared/enums/status';

export const TransactionsFilterBar = memo(function TransactionsFilterBar({
  filters,
  onFilterChange,
  onRefresh,
}: TransactionsFilterBarProps) {
  const { t } = useTranslation();

  const handleStatusChange = useCallback(
    (status: TransactionStatus | '') => onFilterChange({ status }),
    [onFilterChange],
  );

  const handleSearchChange = useCallback(
    (search: string) => onFilterChange({ search }),
    [onFilterChange],
  );

  const handleDateRangeChange = useCallback(
    (dateRange: string) => onFilterChange({ dateRange }),
    [onFilterChange],
  );

  const handleRefundedToggle = useCallback(
    (on: boolean) =>
      onFilterChange({ refundState: on ? 'refunded' : '' }),
    [onFilterChange],
  );

  return (
    <GenericFilterBar
      search={{
        value: filters.search,
        onChange: handleSearchChange,
        placeholder: t('dashboard.transactions.searchPlaceholder'),
        ariaLabel: t('dashboard.transactions.searchPlaceholder'),
        name: 'transaction-search',
        type: 'text',
      }}
      controls={
        <>
          <StatusFilterDropdown
            value={filters.status}
            options={TRANSACTION_STATUS_OPTIONS}
            onChange={handleStatusChange}
            ariaLabel={t('dashboard.filters.aria.filterTransactionsStatus')}
          />
          <DateRangePicker value={filters.dateRange} onChange={handleDateRangeChange} />
          <FilterToggle
            label={t('dashboard.filters.toggles.refundedOnly')}
            icon="undo"
            value={filters.refundState === 'refunded'}
            onChange={handleRefundedToggle}
          />
        </>
      }
      privacyScope="transactions"
      onRefresh={onRefresh}
      refreshAriaLabel={t('dashboard.filters.aria.refreshTransactions')}
    />
  );
});

export default TransactionsFilterBar;
